import { useCallback, useEffect, useState } from "react";
import { listMyLinks, saveLinks } from "@/features/identity/api/linksApi";
import { PLATFORMS, getPlatform } from "@/features/identity/constants/platforms";
import { validateLinks, hasErrors } from "@/features/identity/validation";
import { useUnsavedWarning } from "@/features/identity/hooks/useUnsavedWarning";
import { IDLE } from "@/features/identity/hooks/useSectionForm";
import { friendlyDataError } from "@/lib/errors";

let seq = 0;
const toDraft = (l) => ({ key: l.id, id: l.id, platform: getPlatform(l.platform).key, url: l.url, is_visible: l.is_visible });
const signature = (rows) => JSON.stringify(rows.map((r) => [r.id || null, r.platform, r.url.trim(), r.is_visible]));

export function useLinksEditor() {
  const [original, setOriginal] = useState([]);
  const [rows, setRows] = useState([]);
  const [load, setLoad] = useState({ status: "loading", error: null });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(IDLE);

  const fetchLinks = useCallback(async () => {
    setLoad({ status: "loading", error: null });
    try {
      const data = await listMyLinks();
      setOriginal(data);
      setRows(data.map(toDraft));
      setLoad({ status: "ready", error: null });
    } catch (err) {
      setLoad({ status: "error", error: friendlyDataError(err, "We couldn't load your links.") });
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const dirty = signature(rows) !== signature(original.map(toDraft));
  useUnsavedWarning(dirty);

  const edit = (fn) => {
    setRows(fn);
    setStatus(IDLE);
  };
  const add = (platform) => {
    const key = `new-${++seq}`;
    edit((r) => [...r, { key, id: null, platform, url: "", is_visible: true }]);
  };
  const update = (key, patch) => {
    edit((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x)));
    setErrors((e) => {
      const next = { ...e };
      delete next[key];
      return next;
    });
  };
  const remove = (key) => edit((r) => r.filter((x) => x.key !== key));
  const move = (key, dir) =>
    edit((r) => {
      const i = r.findIndex((x) => x.key === key);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= r.length) return r;
      const next = [...r];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const save = async () => {
    if (load.status !== "ready") return false;
    const found = validateLinks(rows);
    setErrors(found);
    if (hasErrors(found)) {
      setStatus({ state: "invalid", message: "Please check the highlighted links." });
      return false;
    }
    if (!dirty) return true;
    setStatus({ state: "saving", message: null });
    try {
      const fresh = await saveLinks(rows, original);
      setOriginal(fresh);
      setRows(fresh.map(toDraft));
      setStatus({ state: "saved", message: "Links saved." });
      return true;
    } catch (err) {
      await fetchLinks();
      const msg = friendlyDataError(err, "We couldn't save your links.");
      setStatus({ state: "error", message: `${msg} Your saved links were reloaded.` });
      return false;
    }
  };

  const available = PLATFORMS.filter((p) => !rows.some((r) => r.platform === p.key));
  return { rows, errors, status, load, dirty, available, add, update, remove, move, save, reload: fetchLinks };
}
